import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Handshake } from "lucide-react";

export const Enquiry = () => {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/enquiries", { name: form.name || null, contact: form.contact, message: form.message });
      setSent(true);
      toast.success("It's with Matt. He reads every one himself.");
    } catch {
      toast.error("That didn't land. Try again.");
    }
  };

  return (
    <section className="py-24" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <p className="eyebrow mb-4">Not Ready to Book?</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">What's on your mind?</h2>
          <p className="mt-6 text-hearth-muted leading-relaxed max-w-md">
            Three lines or three pages. It goes straight to Matt — no receptionist, no intake bot,
            no newsletter trap. He replies himself.
          </p>
        </div>
        {sent ? (
          <p data-testid="enquiry-success" className="font-essay italic text-2xl text-hearth-ochre self-center">
            Heard. Matt will come back to you personally.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
            <input
              data-testid="enquiry-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name (optional — first name is plenty)"
              className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre"
            />
            <input
              data-testid="enquiry-contact-input"
              required
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="How he reaches you — email or phone"
              className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre"
            />
            <textarea
              data-testid="enquiry-message-input"
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              placeholder="Say it however it comes out."
              className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre font-essay text-lg"
            />
            <button data-testid="enquiry-submit-button" className="inline-flex items-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
              <Mail className="w-4 h-4" /> Send It, Privately
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export const Partners = () => {
  const [form, setForm] = useState({ business_name: "", contact: "", offering: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partners", form);
      setSent(true);
      toast.success("Noted. Matt will be in touch about working together.");
    } catch {
      toast.error("That didn't land. Try again.");
    }
  };

  return (
    <section className="py-24 bg-hearth-surface border-y border-hearth-line" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <p className="eyebrow mb-4">Good Company</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">
            Run an outfit that belongs beside a fire?
          </h2>
          <p className="mt-6 text-hearth-muted leading-relaxed max-w-md">
            Fishing charters, trek guides, campsites, cafés with quiet corners, equine places,
            mens' sheds — if your work puts people at ease, Matt's work probably belongs next to it.
            Partner links live here as they come aboard.
          </p>
          <div className="mt-10 border border-dashed border-hearth-line p-8" data-testid="partners-empty">
            <p className="font-essay italic text-xl text-hearth-muted">The roster is being written. Yours could be the first name on it.</p>
          </div>
        </div>
        {sent ? (
          <p data-testid="partner-success" className="font-essay italic text-2xl text-hearth-ochre self-center">
            Welcome aboard, pending a handshake.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="partner-form">
            <input
              data-testid="partner-business-input"
              required
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="Business name"
              className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre"
            />
            <input
              data-testid="partner-contact-input"
              required
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="Email or phone"
              className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre"
            />
            <textarea
              data-testid="partner-offering-input"
              required
              value={form.offering}
              onChange={(e) => setForm({ ...form, offering: e.target.value })}
              rows={4}
              placeholder="What you do, and how you see it working alongside Matt"
              className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre font-essay text-lg"
            />
            <button data-testid="partner-submit-button" className="inline-flex items-center gap-3 border border-hearth-ochre text-hearth-ochre hover:bg-hearth-ochre hover:text-hearth-bg text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
              <Handshake className="w-4 h-4" /> Put Your Hand Up
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
